













































import { computed, defineComponent, ref, useContext, useFetch, watch } from '@nuxtjs/composition-api';
import axios from 'axios';

import { PropType } from 'vue';
import { useCategoryStore } from '~/modules/catalog/category/stores/category';
import { extendProductsForBox, getProductTypesParentCategory, getTagColor, getTopicsParentCategory } from '~/utils/solepro/products';
import { ProductForProductPageType } from '~/utils/types/ProductPage';
import ProductBox from '~/components/solepro/ProductBox.vue';
import { BaseCategory } from '~/utils/types/ProductPage';
import { Pagination, Navigation } from 'swiper'
import { productUpSellLinkTrack, productUpSellNavTrack } from '~/helpers/trackings/24orepro/products';
import { FEMININE_CATEGORIES } from "~/utils/constants/product";
import { getAxiosConfigs } from '~/utils/solepro/functions';
import { SwiperCore, Swiper, SwiperSlide } from 'swiper-vue2'
import { useConfig } from '~/composables';
import { getItem } from '../../../helpers/asyncLocalStorage';
import { useCustomerStore } from '~/modules/customer/stores/customer';
import { getUserIsLogged } from '~/helpers/user/user';

SwiperCore.use([Pagination, Navigation])


export default defineComponent({
    props: {
        product: {
            type: Object as PropType<ProductForProductPageType>
        }
    },
    components: {
        ProductBox,
        Swiper,
        SwiperSlide
    },
    setup(props, { emit }) {
        const product = props.product;
        const categoryStore = useCategoryStore();
        const { app, $cookies } = useContext();
        const { config } = useConfig();
        const products = ref([]);
        const isFeminine = ref(false);
        const productType = ref<BaseCategory>();
        const productTopic = ref<BaseCategory>();
        const MAX_PRODUCTS = 8;
        const isUserLogged = getUserIsLogged($cookies);
        const customerStore = useCustomerStore()
        const customerOrders = computed(() => {
            const orders = customerStore.user?.orders?.items || [];
            return orders.flatMap(order =>
                (order.items || []).map(item => item.product_sku)
            );
        })
        const upsellSlider = computed(() => {
            if (products.value[0]?._source === "topic" && productTopic.value) {
                return {
                    title: {
                        text: "Altri prodotti di",
                        name: productTopic.value.name,
                        color: productTopic.value.tags_color ? getTagColor(productTopic.value.tags_color) : null,
                    },
                    button: 'topic'
                };
            }

            if (products.value[0]?._source === "type" && productType.value) {
                return {
                    title: {
                        text: isFeminine.value ? "Altre " : "Altri ",
                        name: productType.value.name,
                        color: productType.value.tags_color ? getTagColor(productType.value.tags_color) : null,
                    },
                    button: 'type'
                };
            }

            return {
               title: {
                    text: "Potrebbe interessarti anche...",
                    name: '',
                    color: null,
                },
                button: ''
            };
        });

        const fetchUpsellData = async () => {
            await categoryStore.load();
            const allcategories = categoryStore.rawCategories?.items[0].children;
            const productTopicsUids = getTopicsParentCategory(allcategories).children.map(el => el.uid);
                productTopic.value = product.categories.find(el => productTopicsUids.includes(el.uid)) as BaseCategory;

            const cases = [
                { fn: getMagentoUpsell, key: 'upsell', priority: 4 },
                { fn: getUserOrders, key: 'orders', priority: 3 },
                { fn: getNavigationSkus, key: 'navigation', priority: 2 },
                { fn: getTopicProducts, key: 'topic', priority: 1 },
                { fn: getProductTypeProducts, key: 'type', priority: 0 }
            ];

            const addedSkus = new Set();
            const finalProducts: any[] = [];

            for (const source of cases) {
                if (finalProducts.length >= MAX_PRODUCTS) break;

                try {
                    const results = await source.fn();
                    if (!results || !results.length) continue;

                    for (const p of results) {
                        if (finalProducts.length >= MAX_PRODUCTS) break;

                        if (p.sku === product.sku) continue; // filtering out current sku
                        if (addedSkus.has(p.sku)) continue; // and duplicates

                        addedSkus.add(p.sku);
                        finalProducts.push({
                            ...p,
                            _source: source.key
                        });
                    }
                } catch (e) {
                    console.error(e);
                }
            }

            products.value = extendProductsForBox(allcategories, finalProducts);

            isFeminine.value = productType.value && FEMININE_CATEGORIES.includes(productType.value.name.toLowerCase())
            emit('loaded')
        };

        useFetch(async () => {
            if (!isUserLogged) {
                await fetchUpsellData();
            }
        });

        // for logged in users, wait for customerStore to load
        if (isUserLogged) {
            watch(() => customerStore.user, async (newUser) => {
                    if (newUser) {
                        await fetchUpsellData();
                    }
                },
                { immediate: true }
            );
        }

        const getMagentoUpsell = async () => {
            if (!product.upSell?.length) return [];
            const skus = product.upSell.map(el => el.sku).slice(0, MAX_PRODUCTS);
            return await fetchProductBaseDataForBox({ skus })
        };

        const getUserOrders = async () => {
            if (!isUserLogged) return [];
            if (!customerOrders.value) return [];

           const skus = customerOrders.value;
            const uniqueSkus = [...new Set(skus)].filter(Boolean);

            if (!uniqueSkus.length) return [];

            const data = await fetchProductBaseDataForBox({ skus: uniqueSkus });

            const purchasedProducts = data || [];

            const allcategories = categoryStore.rawCategories?.items[0].children;
            const topicCategories = getTopicsParentCategory(allcategories)?.children || [];

            const topicUidToNameMap: Record<string, string> = {};
            const topicNameToUidMap: Record<string, string> = {};
            topicCategories.forEach(cat => {
                topicUidToNameMap[cat.uid] = cat.name;
                topicNameToUidMap[cat.name] = cat.uid;
            });
            const topicSet = new Set<string>();
            purchasedProducts.forEach(product => {
                (product.categories || []).forEach(cat => {
                    const topicName = topicUidToNameMap[cat.uid];
                    if (topicName) {
                        topicSet.add(topicName);
                    }
                });
            });

            const topics = Array.from(topicSet);
            if (!topics.length) return [];
            const topicUids = topics.map(topic => topicNameToUidMap[topic]).filter(Boolean);

            const recommendedProductsData = await fetchProductBaseDataForBox({ variables: { filter: { category_uid: { in: topicUids } }, pageSize: MAX_PRODUCTS } })

            const addedSkus = new Set();

            const recommendedProducts = (recommendedProductsData || []).filter(rec => {
                if (rec.sku === product.sku) return false; // filtering out current PDP product
                if (uniqueSkus.includes(rec.sku)) return false; // and products included in the user's most recent purchases
                if (addedSkus.has(rec.sku)) return false; // aand duplicates

                addedSkus.add(rec.sku);

                return true;
            });

            const datas = recommendedProducts.slice(0, MAX_PRODUCTS);
            return datas;
        };

        const getTopicProducts = async () => {
            if (!productTopic.value) return [];
            return await fetchProductBaseDataForBox({ variables: { filter: { category_uid: { eq: productTopic.value.uid } }, pageSize: MAX_PRODUCTS } })
        };

        const getProductTypeProducts = async () => {
            /** Se il prodotto è in testing category andiamo a skippare la ricerca per stesso tipo di prodotto */
            if(product.categories.find(c=>c.id==app.$config.exactTestingCategory)) return [];
            await categoryStore.load();
            const allcategories = categoryStore.rawCategories?.items[0].children;
            const productTypeUids = getProductTypesParentCategory(allcategories).children.map(el => el.uid);
            productType.value = product.categories.find(el => productTypeUids.includes(el.uid)) as BaseCategory;

            if (!productType.value) return [];
            return await fetchProductBaseDataForBox({ variables: { filter: { category_uid: { eq: productType.value.uid } }, pageSize: MAX_PRODUCTS} });
        };

        const getNavigationSkus = async () => {
            const skus = await getItem<string[]>('trackedSkus').catch(() => []);
            if (skus && skus.length) {
                const navigationProducts = await fetchProductBaseDataForBox({ skus });
                /** Escludiamo i prodotti in testing category. */
                return navigationProducts.filter(el=>!el.categories.find(c=>c.id == app.$config.exactTestingCategory));
            }
            return [];
        };

        const fetchProductBaseDataForBox = async (payload) => {
            const { data } = await axios.post('/sole-api/product/pro/getProductBaseDataForBox', payload, { ...getAxiosConfigs(app, config) });
            return data || [];
        };

        const swiperProps = {
            slidesPerView: "auto",
            spaceBetween: 12,
            modules: [Navigation, Pagination],
            slideClass: 'slide',
            pagination: {
                el: '.section-up-sell .pagination',
                type: 'progressbar',
                progressbarFillClass: 'bg-black w-full block h-full'
            },
            navigation: {
                prevEl: '.section-up-sell .navigation .prev-btn',
                nextEl: '.section-up-sell .navigation .next-btn',
            },
            breakpoints: {
                1024: {
                    slidesPerView: 3,
                    spaceBetween: 20
                },
                1366: {
                    slidesPerView: 4,
                    spaceBetween: 20
                }
            }
        }

        const trackUpSellLink = (val, callback) => productUpSellLinkTrack(val, callback);
        const trackProductUpSellNav = (val) => productUpSellNavTrack(product, val); 


        return {
            products,
            productType,
            productTopic,
            swiperProps,
            trackProductUpSellNav,
            trackUpSellLink,
            getTagColor,
            isFeminine,
            storeCode: config.value?.store_code,
            upsellSlider
        }
    },
    methods: {
        calculateHeights(swiper) {
            if(!swiper.slides) return;
            const tallest = swiper.slides.reduce((tallest: Element, slide: Element) => {
                return slide.getBoundingClientRect().height > tallest.getBoundingClientRect().height ? slide : tallest;
            });
            const h = tallest.offsetHeight + 'px';
            swiper.slides.forEach(el => el.style.height = h)
            
            //document.querySelectorAll('.slide').forEach(slide=>slide.style.height = h+'px')
        },
        afterInit(swiper) {
             /** UGLY, VERY UGLY Try to check why the resize / swiper don't work as espected */
             const interval = setInterval(()=>{
                const slides = swiper.$el[0].querySelectorAll('.slide');
                if(slides.length>1){
                    clearInterval(interval);
                    setTimeout(()=>{
                        swiper.update();
                console.log('updated');
                this.calculateHeights(swiper)
                    },1000)
                }
            },300)
            setTimeout(() => {
                clearInterval(interval)
            }, 10000)
        }
    }
})
